import type { ScaledIngredient } from '@recipes/contracts';
import { formatIngredientLine } from '@recipes/domain';
import { Checkbox, cn } from '@recipes/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '@/lib/i18n';

export interface IngredientLineData {
  id: string;
  name: string;
  note?: string | null;
  optional?: boolean;
  quantity: string | null;
  quantityMax: string | null;
  quantityText?: string | null;
  unitCode: string | null;
  unitText?: string | null;
  /** Pre-formatted display from the scale-preview endpoint; computed locally when absent. */
  display?: string;
  hint?: string | null;
  converted?: boolean;
}

export interface IngredientLineProps {
  ingredient: IngredientLineData | ScaledIngredient;
  /** Render a checkbox (cooking mode / shopping). */
  checkable?: boolean;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  size?: 'default' | 'lg';
  className?: string;
  /** Shown after the text and outside the checkbox label, e.g. the link to a base recipe. */
  trailing?: ReactNode;
}

/** One ingredient row: amount (fraction display), name, note, optional marker and hint. */
export function IngredientLine({
  ingredient,
  checkable,
  checked,
  onCheckedChange,
  size = 'default',
  className,
  trailing,
}: IngredientLineProps) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const computed =
    'display' in ingredient && ingredient.display !== undefined
      ? { amount: ingredient.display, hint: ingredient.hint ?? null }
      : (() => {
          const line = formatIngredientLine(
            {
              quantity: ingredient.quantity,
              quantityMax: ingredient.quantityMax,
              quantityText: ingredient.quantityText ?? null,
              unitCode: ingredient.unitCode,
              unitText: ingredient.unitText ?? null,
              name: ingredient.name,
            },
            locale,
          );
          return { amount: line.amount, hint: line.hint };
        })();
  // A volume that became a weight (or back) through the ingredient's density is an estimate.
  const approximate = 'approximate' in ingredient && ingredient.approximate === true;
  const amount =
    approximate && computed.amount
      ? `${t('recipes.detail.approximate')} ${computed.amount}`
      : computed.amount;
  const showAmountSeparately = amount && !amount.includes(ingredient.name);
  const id = `ingredient-${ingredient.id}`;

  const content = (
    <span className={cn('flex-1', checked && 'line-through text-muted-foreground')}>
      {showAmountSeparately && (
        <span className="font-semibold tabular-nums" data-testid="ingredient-amount">
          {amount}{' '}
        </span>
      )}
      <span data-testid="ingredient-name">
        {showAmountSeparately ? ingredient.name : amount || ingredient.name}
      </span>
      {ingredient.note && <span className="text-muted-foreground">, {ingredient.note}</span>}
      {ingredient.optional && (
        <span className="ml-1 text-xs text-muted-foreground">({t('common.optional')})</span>
      )}
      {'converted' in ingredient && ingredient.converted && (
        <span className="ml-1 text-xs text-muted-foreground">
          ({t('recipes.detail.converted')})
        </span>
      )}
      {computed.hint && (
        <span className="block text-xs text-muted-foreground" data-testid="ingredient-hint">
          {computed.hint}
        </span>
      )}
    </span>
  );

  if (!checkable) {
    return (
      <li
        className={cn(
          'flex flex-wrap items-center gap-2 py-1.5',
          size === 'lg' && 'text-lg py-2',
          className,
        )}
        data-testid="ingredient-line"
      >
        {content}
        {trailing}
      </li>
    );
  }
  return (
    <li
      className={cn(
        'flex flex-wrap items-start gap-3',
        size === 'lg' ? 'py-2 text-lg' : 'py-1.5',
        className,
      )}
      data-testid="ingredient-line"
    >
      <Checkbox
        id={id}
        checked={!!checked}
        onCheckedChange={(v) => onCheckedChange?.(v === true)}
        size={size === 'lg' ? 'lg' : 'default'}
        className={cn('mt-0.5', size === 'lg' && 'touch-target')}
        aria-label={`${amount} ${ingredient.name}`.trim()}
      />
      <label htmlFor={id} className="flex-1 cursor-pointer select-none">
        {content}
      </label>
      {trailing}
    </li>
  );
}
